/* istanbul ignore file */
/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import ChannelService from '@avtechsoftware/ra-javascript-utilities/src/utilities/channels/ChannelService';
import BaseTemperatureChannel
  from '@avtechsoftware/ra-javascript-utilities/src/classes/channels/BaseTemperatureChannel/BaseTemperatureChannel';
import ChannelGroupType from '@avtechsoftware/ra-javascript-utilities/src/types/ChannelGroupType';
import ChannelType from '@avtechsoftware/ra-javascript-utilities/src/types/ChannelType';
import {
  adjustFromDisplay,
  getDefaultAdapterPortLabel,
  normalizeRaaChannelKey,
  parseChannelKey,
  trimRaaChannelKey,
} from '../../../../../../../utility/channelUtils';
import { CHANNEL_TYPE_ORDER, getReadingCardComponent } from '../../common/channelGroupTypeToComponentMap';
import { CrudStatus as AvtechApiStatus } from '../../../../../../../utility/types/CrudStatus';
import {
  fetchSingleDevice,
  resetDeviceDataFormState,
  selectSensorUpdateStatus,
  updateSensor,
} from '../../../../../../../redux/slices/singleDeviceSlice/singleDeviceSlice';
import { selectSessionInformation } from '../../../../../../../redux/slices/sessionSlice';
import sensorSettingsModalFactory from '../../../../modals/settings/sensorSettingsModalFactory';
import formatTimestamp from '../../../../../../../utility/formatTimestamp';

const AdapterSensorCard = ({
  channelGroup, isHidden, isMuted, isReadOnly,
}) => {
  const { lastReceived } = channelGroup || '';
  const dispatch = useDispatch();
  const saveStatus = useSelector(selectSensorUpdateStatus);
  const { temperatureScale, timezone, activeSubscriptionId } = useSelector(selectSessionInformation);
  const preferencesDisplay = { temperatureScale };
  const parsedChannelKey = parseChannelKey(channelGroup.channelKey);
  const ReadingCardComponent = getReadingCardComponent(channelGroup);
  // eslint-disable-next-line no-unused-vars
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (saveStatus === AvtechApiStatus.SUCCESS) {
      setShowModal(false);
      dispatch(resetDeviceDataFormState());
    }
  }, [dispatch, saveStatus]);

  const handleCloseModal = () => {
    setShowModal(false);
    dispatch(resetDeviceDataFormState());
  };

  const handleSave = async (data) => {
    try {
      const updatedChannels = channelGroup.channels.map((channel) => {
        const parsedKey = parseChannelKey(channel.channelKey);
        const channelInstance = ChannelService.getChannelGroupChannel(
          parsedKey.channelGroupType,
          parsedKey.channelIndex,
          parsedKey.channelType,
        );
        let adjust = 0;
        if (data.channelData[channel.channelKey]
            && data.channelData[channel.channelKey].adjust !== undefined) {
          adjust = parseInt(data.channelData[channel.channelKey].adjust, 10);
        }
        const normalizedAdjust = adjustFromDisplay(
          channelInstance,
          adjust,
          preferencesDisplay.temperatureScale,
        );
        // Include conversion if it exists in the form data
        // let conversion = { type: ConversionType.NONE };
        // if (data.channelData[channel.channelKey]
        //     && data.channelData[channel.channelKey].conversion) {
        //   conversion = data.channelData[channel.channelKey].conversion;
        // }
        return {
          channelKey: trimRaaChannelKey(channel.channelKey),
          adjust: normalizedAdjust,
          // conversion,
        };
      });
      const payload = {
        channelKey: channelGroup.channelKey,
        channels: updatedChannels,
        label: data.label,
        isHidden: data.isHidden,
      };
      await dispatch(updateSensor(payload)).unwrap();
      await dispatch(fetchSingleDevice()).unwrap();
      return true;
    } catch (error) {
      throw new Error(error.message || 'Failed to save sensor settings');
    }
  };

  if (ReadingCardComponent) {
    const sortedChannels = [...channelGroup.channels]
      .filter((c) => {
        const { channelType } = parseChannelKey(c.channelKey);
        return channelType !== ChannelType.UNKNOWN.id && channelType !== ChannelType.PUSH_STATUS.id && channelType !== ChannelType.ALARM.id;
      })
      .sort((a, b) => {
        const aType = parseChannelKey(a.channelKey).channelType;
        const bType = parseChannelKey(b.channelKey).channelType;
        return (CHANNEL_TYPE_ORDER[aType] || Infinity) - (CHANNEL_TYPE_ORDER[bType] || Infinity);
      });

    let readingNumber = 1;

    const readings = sortedChannels.reduce((acc, channel) => {
      const parsedKey = parseChannelKey(channel.channelKey);
      const channelInstance = ChannelService.getChannelGroupChannel(
        parsedKey.channelGroupType,
        parsedKey.channelIndex,
        parsedKey.channelType,
      );

      if (parsedKey.channelGroupType === ChannelGroupType.EXTREME_TEMP.id && parsedKey.channelType === ChannelType.TEMPERATURE.id && parsedKey.channelIndex === 1) {
        const faultChannel = sortedChannels.find((ch) => parseChannelKey(ch.channelKey).channelType === ChannelType.EXTREME_TEMPERATURE_FAULT.id);
        if (faultChannel && faultChannel.value > 0) {
          return acc;
        }
      }

      const scale = channelInstance instanceof BaseTemperatureChannel ? preferencesDisplay.temperatureScale : preferencesDisplay.unitOfSpeed;
      if (parsedKey.channelType === ChannelType.ANALOG_COMPUTED.id) {
        acc[`reading${readingNumber}`] = {
          value: channelInstance.getDisplayValue(channel.value, { scale }),
          units: channelInstance.getUnits({ customUnits: channel.conversion.units }),
          title: channelInstance.displayName,
          isTriggered: null,
          channelKey: normalizeRaaChannelKey(channel.channelKey, activeSubscriptionId),
        };
      } else {
        acc[`reading${readingNumber}`] = {
          value: channelInstance.getDisplayValue(channel.value, { scale }),
          units: channelInstance.getUnits({ scale }),
          title: channelInstance.displayName,
          isTriggered: null,
          channelKey: normalizeRaaChannelKey(channel.channelKey, activeSubscriptionId),
        };
      }

      readingNumber += 1;
      return acc;
    }, {});
    const cgType = ChannelService.getChannelGroupTypeByValueId(parsedChannelKey.channelGroupType);
    const ModalComponent = sensorSettingsModalFactory(cgType.id);
    return (
      <div>
        <ReadingCardComponent
          sensorName={channelGroup.label}
          typeName={getDefaultAdapterPortLabel(parsedChannelKey.channelGroupIndex)}
          macAddress={parsedChannelKey.sensorMacAddress}
          lastUpdated={formatTimestamp(lastReceived.epochSecond, timezone, 'YYYY-MM-DD HH:mm:ss')}
          isWireless={false}
          isHub={false}
          onSettingsClick={isReadOnly ? () => {} : () => setShowModal(true)}
          isMuted={isMuted}
          {...readings}
        />
        <ModalComponent
          show={showModal}
          handleClose={handleCloseModal}
          sensorLabel={channelGroup.label}
          channelGroup={channelGroup}
          onSave={handleSave}
          isHidden={isHidden}
        />
      </div>
    );
  }
  return null;
};

AdapterSensorCard.propTypes = {
  channelGroup: PropTypes.shape({
    label: PropTypes.string.isRequired,
    channelKey: PropTypes.string.isRequired,
    channels: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.number.isRequired,
      channelKey: PropTypes.string.isRequired,
    })).isRequired,
  }).isRequired,
  isHidden: PropTypes.bool.isRequired,
  isMuted: PropTypes.bool.isRequired,
  isReadOnly: PropTypes.bool.isRequired,
};

export default AdapterSensorCard;
